import { useEffect, useState } from 'react';

export const PaymentStatus = {
  COMPLETED: 'COMPLETED',
  SCHEDULED: 'SCHEDULED',
  REFUNDED: 'REFUNDED',
  CANCELLED: 'CANCELLED',
} as const;

export interface PaymentFormData {
  paymentIdx?: number;
  amount?: number;
  paidDate?: string;
  type?: string;
  method?: string;
  customerIdx?: number;
  bankAccount?: string;
  memo?: string;
  status?: string;
}

export interface PaymentCreatePayload {
  bankAccount?: string;
  memo?: string;
  payments: Array<{
    paidDate: string;
    type: string;
    amount: number;
    status?: string;
    method?: string;
  }>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PaymentFormData | PaymentCreatePayload) => void;
  initialData: PaymentFormData | null;
}

type BundleRow = {
  paidDate: string;
  type: string;
  amount: number;
  status?: string;
  method?: string;
};

const newRow = (type = 'CONTRACT'): BundleRow => ({
  paidDate: new Date().toISOString().split('T')[0],
  type,
  amount: 0,
  status: PaymentStatus.COMPLETED,
  method: 'TRANSFER',
});

export default function PaymentFormModal({ isOpen, onClose, onSave, initialData }: Props) {
  const [formData, setFormData] = useState<PaymentFormData>({
    amount: 0,
    paidDate: new Date().toISOString().split('T')[0],
    type: 'CONTRACT',
    method: 'TRANSFER',
    bankAccount: '',
    memo: '',
    status: PaymentStatus.COMPLETED,
  });

  const [commonData, setCommonData] = useState({
    bankAccount: '',
    memo: '',
  });
  const [rows, setRows] = useState<BundleRow[]>([newRow('CONTRACT')]);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setFormData({
        amount: initialData.amount || 0,
        paidDate: initialData.paidDate || new Date().toISOString().split('T')[0],
        type: initialData.type || 'CONTRACT',
        method: initialData.method || 'TRANSFER',
        bankAccount: initialData.bankAccount || '',
        memo: initialData.memo || '',
        status: initialData.status || PaymentStatus.COMPLETED,
      });
      return;
    }

    setCommonData({
      bankAccount: '',
      memo: '',
    });
    setRows([newRow('CONTRACT')]);
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (initialData) {
      if (!formData.paidDate || (formData.amount || 0) <= 0) {
        alert('입금일자와 금액을 정확히 입력해주세요.');
        return;
      }
      onSave(formData);
      return;
    }

    const validRows = rows.filter((row) => row.paidDate && row.type && row.amount > 0);
    if (validRows.length === 0) {
      alert('최소 1개의 입금 항목(입금일자/타입/금액)을 입력해주세요.');
      return;
    }

    onSave({
      bankAccount: commonData.bankAccount || undefined,
      memo: commonData.memo || undefined,
      payments: validRows.map((row) => ({
        paidDate: row.paidDate,
        type: row.type,
        amount: Number(row.amount),
        status: row.status || undefined,
        method: row.method || undefined,
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-[640px] p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 text-gray-800">
          {initialData ? '입금 내역 수정' : '입금 내역 등록'}
        </h3>

        {initialData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">입금일자</label>
                <input
                  type="date"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.paidDate}
                  onChange={(e) => setFormData({ ...formData, paidDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                <select
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.status ?? PaymentStatus.COMPLETED}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="COMPLETED">입금 완료</option>
                  <option value="SCHEDULED">입금 예정</option>
                  <option value="REFUNDED">환불됨</option>
                  <option value="CANCELLED">취소됨</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">입금구분</label>
                <select
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="CONTRACT">계약금</option>
                  <option value="INTERMEDIATE">중도금</option>
                  <option value="BALANCE">잔금</option>
                  <option value="ETC">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">금액</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">결제수단</label>
                <select
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                >
                  <option value="TRANSFER">계좌이체</option>
                  <option value="CARD">카드</option>
                  <option value="CASH">현금</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">입금계좌 / 정보</label>
                <input
                  type="text"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={formData.bankAccount}
                  onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
              <input
                type="text"
                className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                value={formData.memo}
                onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-sm text-gray-600 mb-1">공통 계좌/정보</label>
                <input
                  type="text"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={commonData.bankAccount}
                  onChange={(e) => setCommonData({ ...commonData, bankAccount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">공통 메모</label>
                <input
                  type="text"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                  value={commonData.memo}
                  onChange={(e) => setCommonData({ ...commonData, memo: e.target.value })}
                />
              </div>
            </div>

            {rows.map((row, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end border border-gray-200 rounded-lg p-3"
              >
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">입금일자</label>
                  <input
                    type="date"
                    className="w-full min-w-[150px] border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                    value={row.paidDate}
                    onChange={(e) => {
                      const next = [...rows];
                      next[index] = { ...next[index], paidDate: e.target.value };
                      setRows(next);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">타입</label>
                  <select
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                    value={row.type}
                    onChange={(e) => {
                      const next = [...rows];
                      next[index] = { ...next[index], type: e.target.value };
                      setRows(next);
                    }}
                  >
                    <option value="CONTRACT">계약금</option>
                    <option value="INTERMEDIATE">중도금</option>
                    <option value="BALANCE">잔금</option>
                    <option value="ETC">기타</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">금액</label>
                  <input
                    type="number"
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                    value={row.amount}
                    onChange={(e) => {
                      const next = [...rows];
                      next[index] = { ...next[index], amount: Number(e.target.value) };
                      setRows(next);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">상태</label>
                  <select
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                    value={row.status || PaymentStatus.COMPLETED}
                    onChange={(e) => {
                      const next = [...rows];
                      next[index] = { ...next[index], status: e.target.value };
                      setRows(next);
                    }}
                  >
                    <option value="COMPLETED">입금 완료</option>
                    <option value="SCHEDULED">입금 예정</option>
                    <option value="REFUNDED">환불됨</option>
                    <option value="CANCELLED">취소됨</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">입금방법</label>
                  <select
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none"
                    value={row.method || 'TRANSFER'}
                    onChange={(e) => {
                      const next = [...rows];
                      next[index] = { ...next[index], method: e.target.value };
                      setRows(next);
                    }}
                  >
                    <option value="TRANSFER">계좌이체</option>
                    <option value="CARD">카드</option>
                    <option value="CASH">현금</option>
                  </select>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                    disabled={rows.length === 1}
                    className="w-full px-3 py-2 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setRows((prev) => [...prev, newRow('ETC')])}
              className="px-3 py-2 bg-white border border-teal-300 text-teal-700 rounded hover:bg-teal-50"
            >
              + 항목 추가
            </button>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors cursor-pointer"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
